import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setEdges } from '../features/mapEdges/mapEdgesSlice';
import { drawPoints } from './mapMarkers';
import carIcon from '../icons/car.png';

const MapController = ({ map, mode = 'view', serverPoints = [], onMarkerClick }) => {
  const dispatch = useDispatch();
  const initialBoundsSet = useRef(false);
  const placemarksRef = useRef([]);
  const serverMarkersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const listenerRef = useRef(null);

  const addPlacemark = (lat, lng, options = {}) => {
    if (!map || !window.ymaps3) return null;
    const { isUser = false } = options;
    const markerElement = document.createElement('div');
    markerElement.style.width = '68px';
    markerElement.style.height = '92px';
    markerElement.style.position = 'relative';
    markerElement.style.transform = 'translate(-50%, -100%)';
    markerElement.style.cursor = 'pointer';
    markerElement.innerHTML = `
      <svg width="68" height="92" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="position:absolute;left:0;top:0;width:100%;height:100%;">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ffffff" stroke="#000000" stroke-width="0.7"/>
      </svg>
      <img src="${carIcon}" alt="car" style="position:absolute;left:50%;top:36%;width:34px;height:34px;transform:translate(-50%,-50%);object-fit:contain;padding:4px;" />
    `;
    const marker = new window.ymaps3.YMapMarker(
      { coordinates: [lng, lat], draggable: false, mapFollowsOnDrag: false, zIndex: isUser ? 1500 : 1000 },
      markerElement
    );
    map.addChild(marker);
    placemarksRef.current.push(marker);
    if (isUser) userMarkerRef.current = marker;
    return marker;
  };

  const clearPlacemarks = () => {
    placemarksRef.current.forEach((marker) => { if (map) map.removeChild(marker); });
    placemarksRef.current = [];
    serverMarkersRef.current.forEach((m) => { if (map) map.removeChild(m); });
    serverMarkersRef.current = [];
    if (userMarkerRef.current && map) {
      map.removeChild(userMarkerRef.current);
      userMarkerRef.current = null;
    }
  };

  const renderServerPoints = (points = []) => {
    serverMarkersRef.current.forEach((m) => { if (map) map.removeChild(m); });
    serverMarkersRef.current = [];
    if (!Array.isArray(points) || points.length === 0) return;
    const markers = drawPoints(map, points, {
      interactive: mode === 'view',
      carIconSrc: carIcon,
      onMarkerClick: (driverId) => {
        if (onMarkerClick) onMarkerClick(driverId);
      }
    });
    serverMarkersRef.current = markers;
  };

  useEffect(() => {
    if (!map || !window.ymaps3) return;

    const mapListener = new window.ymaps3.YMapListener({
      layer: 'any',
      onClick: (_object, event) => {
        if (!event?.coordinates) return;
        const [lng, lat] = event.coordinates;
        if (mode === 'place') {
          if (userMarkerRef.current) map.removeChild(userMarkerRef.current);
          addPlacemark(lat, lng, { isUser: true });
          return;
        }
      },
      onUpdate: ({ location }) => {
        const bounds = location.bounds;
        if (bounds) {
          const west = bounds[0][0];
          const south = bounds[0][1];
          const east = bounds[1][0];
          const north = bounds[1][1];
          const centerLat = (south + north) / 2;
          const centerLng = (west + east) / 2;
          dispatch(setEdges({
            left: { lat: centerLat, lng: west },
            right: { lat: centerLat, lng: east },
            top: { lat: north, lng: centerLng },
            bottom: { lat: south, lng: centerLng }
          }));
        }
      },
    });

    listenerRef.current = mapListener;
    map.addChild(mapListener);

    const setInitialBounds = (lat, lng) => {
      map.setLocation({ center: [lng, lat], zoom: 13 });
      initialBoundsSet.current = true;
    };

    if (!initialBoundsSet.current) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => setInitialBounds(position.coords.latitude, position.coords.longitude),
          () => setInitialBounds(55.751244, 37.618423)
        );
      } else {
        setInitialBounds(55.751244, 37.618423);
      }
    }

    renderServerPoints(serverPoints);

    return () => {
      if (map && listenerRef.current) map.removeChild(listenerRef.current);
      clearPlacemarks();
    };
  }, [map, dispatch, serverPoints, mode, onMarkerClick]);

  return null;
};

export default MapController;